import type { ApiFailure } from '../api.js';
import { Button, SelectField } from '../ui/index.js';
import { PROJECT_FILTER_FAILED_HINT, PROJECT_NONE_FILTER_LABEL, PROJECT_NONE_PARAM_VALUE, projectFilterPlaceholder, type ProjectFilterOption } from './lib/todos.js';

// The Project filter (ADR 0017, design §7.7) the To-dos and Meetings bars share: every live own project by name, and
// "No project" for the records filed under none. The bar's CSS (pages.css) sizes it as it sizes the other structured
// selects, by its class.

export interface ProjectFilterFieldProps {
  value: string | null;
  onChange: (project: string | null) => void;
  /** The reader's own live projects; null while they load or after their load failed. */
  options: readonly ProjectFilterOption[] | null;
  /** The projects load's failure, when it failed; the select then says so beside it, with a retry. */
  failure?: ApiFailure | null;
  onRetry?: () => void;
}

/**
 * Why the failure is said beside the select (app-baseline 05-client §5.4 and §5.5; Phase 1c finding D18): the select
 * waits on the projects, and a failed load once left it reading "Loading projects…" for good. Now the select names the
 * failure, stays inoperative, and the adjacent hint — the reason a keyboard and a pointer both reach — offers the retry.
 */
export function ProjectFilterField({ value, onChange, options, failure = null, onRetry }: ProjectFilterFieldProps) {
  const failed = options === null && failure !== null;
  return (
    <SelectField
      label="Project"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value || null)}
      placeholderOption={projectFilterPlaceholder(options, failed)}
      options={[{ value: PROJECT_NONE_PARAM_VALUE, label: PROJECT_NONE_FILTER_LABEL }, ...(options ?? []).map((option) => ({ value: option.id, label: option.name }))]}
      disabled={options === null}
      hint={failed ? (
        <>
          {PROJECT_FILTER_FAILED_HINT}
          {onRetry ? <>{' '}<Button variant="link" size="sm" onClick={onRetry}>Try again</Button></> : null}
        </>
      ) : undefined}
      className="filter-bar__project"
    />
  );
}
